import React, { createContext, useCallback, useContext, useState } from 'react';

/**
 * Consent for embedded third-party media (Google Maps, YouTube).
 *
 * Model: strict opt-in. Nothing is loaded from Google until the visitor
 * actively consents — either globally via the banner, or per-element via a
 * ConsentGate. The choice is stored locally (no server-side profile) and can
 * be withdrawn anytime via the "Cookie-Einstellungen" link in the footer.
 *
 * This mirrors what the Datenschutzerklärung promises and satisfies
 * § 165 Abs 3 TKG 2021 + DSGVO (prior, informed, freely-given consent).
 */
export type ConsentStatus = 'unknown' | 'granted' | 'denied';

const STORAGE_KEY = 'ct-consent-external-media';
const CONSENT_VERSION = 1;

interface StoredConsent {
  status: 'granted' | 'denied';
  version: number;
  timestamp: string;
}

interface ConsentContextValue {
  externalMedia: ConsentStatus;
  allow: () => void;
  deny: () => void;
  reopen: () => void;
}

const ConsentContext = createContext<ConsentContextValue | undefined>(undefined);

function readStored(): ConsentStatus {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return 'unknown';
    const parsed = JSON.parse(raw) as StoredConsent;
    // A version bump invalidates old consent so visitors are asked again.
    if (parsed.version !== CONSENT_VERSION) return 'unknown';
    return parsed.status === 'granted' ? 'granted' : 'denied';
  } catch {
    return 'unknown';
  }
}

export const ConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Lazy init reads the persisted choice synchronously so already-decided
  // visitors never see a flash of the banner.
  const [externalMedia, setExternalMedia] = useState<ConsentStatus>(() => readStored());

  const persist = useCallback((status: 'granted' | 'denied') => {
    try {
      const record: StoredConsent = {
        status,
        version: CONSENT_VERSION,
        timestamp: new Date().toISOString(),
      };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch {
      /* storage blocked — consent then applies for this session only */
    }
  }, []);

  const allow = useCallback(() => {
    persist('granted');
    setExternalMedia('granted');
  }, [persist]);

  const deny = useCallback(() => {
    persist('denied');
    setExternalMedia('denied');
  }, [persist]);

  // Re-show the banner so the visitor can (re)decide. Clearing the stored
  // choice means a fresh, unbiased prompt; the new choice overwrites it.
  const reopen = useCallback(() => {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    setExternalMedia('unknown');
  }, []);

  return (
    <ConsentContext.Provider value={{ externalMedia, allow, deny, reopen }}>
      {children}
    </ConsentContext.Provider>
  );
};

export function useConsent(): ConsentContextValue {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return ctx;
}
